import React, { useEffect, useState } from 'react';
import { useAuthentication, useUser } from '../hooks';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountLogOutIcon from '@mui/icons-material/PowerSettingsNew';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CallIcon from '@mui/icons-material/Call';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { useNavigate } from 'react-router-dom';
import { COLORS, TYPOGRAPHY } from '../styles/theme';

// Styled components for AccountMenu - preserving original colors and styles
const StyledUserNameGrid = styled(Grid)(() => ({
  width: '200px',
  marginBottom: '0',
  paddingLeft: '14px',
  alignItems: 'center',
  display: 'flex',
  '&:hover': {
    backgroundColor: COLORS.ACCOUNT_HOVER,
  },
}));

const StyledSettingsGrid = styled(Grid)(() => ({
  alignItems: 'center',
  display: 'flex',
  position: 'relative',
  '&:hover': {
    backgroundColor: COLORS.ACCOUNT_HOVER,
  },
}));

const StyledSettingsIcon = styled(IconButton)(() => ({
  backgroundColor: COLORS.TRANSPARENT,
}));

const StyledExpandMoreGrid = styled(IconButton)(() => ({
  marginLeft: '10px',
}));

const StyledDivider = styled(Divider)(() => ({
  backgroundColor: COLORS.ACCOUNT_DIVIDER,
  width: '1px',
}));

const StyledUserName = styled('span')(() => ({
  fontSize: TYPOGRAPHY.ACCOUNT_USERNAME_SIZE,
  marginBottom: '1px',
  alignSelf: 'flex-start',
  marginTop: '7px',
  fontFamily: TYPOGRAPHY.ACCOUNT_USERNAME_FONT,
}));

const StyledCallIcon = styled(CallIcon)(() => ({
  height: '15px !important',
  marginRight: '5px !important',
  width: '15px !important',
  marginBottom: '-2px !important',
  color: `${COLORS.PRIMARY_BLUE} !important`,
}));

const StyledExtensionNumber = styled('span')(() => ({
  marginLeft: '1px',
  color: COLORS.WHITE,
  fontSize: TYPOGRAPHY.ACCOUNT_USERNAME_SIZE,
  fontFamily: TYPOGRAPHY.ACCOUNT_EXTENSION_FONT,
}));

const StyledUserNameGridInner = styled(Grid)(() => ({
  display: 'flex',
}));

export const AccountMenu = () => {
  const { auth } = useAuthentication();
  const navigate = useNavigate();
  const { userExtension } = useUser();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUserName] = useState<string | null>(null);

  useEffect(() => {
    const isUserSignedIn = auth.isUserSignedIn();
    setIsLoggedIn(isUserSignedIn);
    setUserName(auth.getUserName());
  }, [auth]);

  const handleLogOutClick = () => {
    auth.signOut();
  };

  const handleLogInClick = () => {
    auth.getSession();
  };

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const [hover, setHover] = useState(false);

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  return (
    <>
      <StyledDivider orientation="vertical" flexItem />
      <StyledSettingsGrid>
        <StyledSettingsIcon
          color="inherit"
          disableFocusRipple
          disableRipple
          aria-label="account-seetings"
          data-testid="settings"
          style={{ backgroundColor: COLORS.TRANSPARENT }}
          onClick={() => navigate('/app/settings')}
        >
          <SettingsIcon fontSize="small" style={{ height: '20px' }} />
        </StyledSettingsIcon>
      </StyledSettingsGrid>
      <Divider
        orientation="vertical"
        flexItem
        sx={{ backgroundColor: COLORS.ACCOUNT_DIVIDER, width: '1px' }}
      />
      <StyledUserNameGrid>
        <StyledUserNameGridInner>
          <>
            <div>
              <StyledUserName>{username}</StyledUserName> <br />
              {userExtension && (
                <>
                  <StyledCallIcon
                    fontSize="small"
                    color="primary"
                    style={{ backgroundColor: COLORS.TRANSPARENT }}
                  />
                  <StyledExtensionNumber>{userExtension}</StyledExtensionNumber>
                </>
              )}
            </div>
            <StyledExpandMoreGrid
              edge="end"
              color="inherit"
              disableFocusRipple
              disableRipple
              aria-label="account"
              data-testid="account-button"
              onClick={handleClick}
              ref={anchorRef}
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              style={{ backgroundColor: COLORS.TRANSPARENT }}
            >
              <AccountExpandMoreIcon fontSize="small" />
            </StyledExpandMoreGrid>
          </>
        </StyledUserNameGridInner>
      </StyledUserNameGrid>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={{ marginTop: '8px', position: 'fixed' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            style: {
              width: '15%',
              backgroundColor: COLORS.ACCOUNT_DIVIDER,
            },
          },
        }}
      >
        <Divider
          orientation="horizontal"
          flexItem
          style={{
            backgroundColor: COLORS.ACCOUNT_DIVIDER,
            width: '1px',
            height: '1px',
          }}
        />
        {isLoggedIn && (
          <Button
            variant="contained"
            style={{
              fontSize: TYPOGRAPHY.ACCOUNT_BUTTON_SIZE,
              fontFamily: TYPOGRAPHY.ACCOUNT_BUTTON_FONT,
              width: '100%',
              height: '50px',
              backgroundColor:
                hover === true
                  ? COLORS.ACCOUNT_BUTTON_HOVER
                  : COLORS.ACCOUNT_BUTTON_BG,
              color: COLORS.WHITE,
              padding: '5px 10px',
            }}
            startIcon={<AccountLogOutIcon />}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleLogOutClick}
            data-testid="logout"
          >
            LOG OUT
          </Button>
        )}
        {!isLoggedIn && (
          <Button
            variant="contained"
            style={{
              fontSize: TYPOGRAPHY.ACCOUNT_BUTTON_SIZE,
              fontFamily: TYPOGRAPHY.ACCOUNT_BUTTON_FONT,
              width: '100%',
              height: '50px',
              backgroundColor:
                hover === true
                  ? COLORS.ACCOUNT_BUTTON_HOVER
                  : COLORS.ACCOUNT_BUTTON_BG,
              color: COLORS.WHITE,
            }}
            startIcon={<AccountLogOutIcon />}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleLogInClick}
            data-testid="login"
          >
            LOG IN
          </Button>
        )}
      </Popover>
    </>
  );
};
